import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../connector/api';
import Header from '../components/Header';

export default function LockedAccounts() {
    const [lockedUsers, setLockedUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch the list of locked accounts on component mount
    const fetchLockedAccounts = async () => {
        setLoading(true);
        try {
            const res = await api.get('admin/locked-accounts/');
            setLockedUsers(res.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load system lockouts.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLockedAccounts();
    }, []);

    // Handle unlocking a user manually
    const handleUnlock = async (id) => {
        try {
            await api.delete(`admin/locked-accounts/${id}/unlock/`);
            toast.success("Account access restored successfully!");
            // Filter out the unlocked user locally to refresh UI instantly
            setLockedUsers(prev => prev.filter(user => user.id !== id));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not unlock account.");
        }
    };

    return (
        <div className="min-h-screen bg-slate-50/60 font-dynamic-title flex flex-col">
            <Header />
            <div className="max-w-7xl w-full mx-auto p-4 sm:p-6 lg:p-8 flex flex-col gap-6 flex-1">
                {/* Page Header */}
                
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <span className="text-[8px] font-bold text-[#002855] uppercase tracking-wider bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200/40">
                            Core Administration
                        </span>
                        <h1 className="text-lg font-extrabold text-slate-900 tracking-tight mt-1.5">Security Control Center</h1>
                        <p className="text-xs text-slate-500 mt-0.5">Review and override temporary security bans across your portal ecosystem.</p>
                    </div>
                    <button 
                        onClick={fetchLockedAccounts}
                        className="px-4 py-2 text-xs font-semibold bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                    >
                        Refresh List
                    </button>
                </div>

                {/* Main Content Area */}
                {loading ? (
                    <div className="flex justify-center items-center h-48">
                        <svg className="animate-spin h-6 w-6 text-[#002855] dark:text-blue-500" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                    </div>
                ) : lockedUsers.length === 0 ? (
                    <div className="p-12 text-center rounded-xl bg-white dark:bg-gray-800 border border-dashed border-gray-200 dark:border-gray-700">
                        <svg className="w-10 h-10 mx-auto text-green-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300">No active blocks</h3>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">All user network points are cleared with zero lockout policies running.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 shadow-xl rounded-xl">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-900/50 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                    <th className="px-6 py-4">Target Identity</th>
                                    <th className="px-6 py-4">IP Footprint</th>
                                    <th className="px-6 py-4 text-center">Failures</th>
                                    <th className="px-6 py-4">Suspension Window</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50 text-xs">
                                {lockedUsers.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-900/20 transition-colors">
                                        <td className="px-6 py-4 font-semibold text-gray-900 dark:text-gray-200">
                                            {user.username || "Anonymous Request"}
                                        </td>
                                        <td className="px-6 py-4 font-mono text-gray-500 dark:text-gray-400">
                                            {user.ip_address}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className="inline-block bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 font-bold px-2 py-0.5 rounded-md">
                                                {user.failures}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-gray-500 dark:text-gray-400">
                                            {user.last_attempt}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                onClick={() => handleUnlock(user.id)}
                                                className="px-3 py-1.5 bg-[#002855] text-white dark:bg-blue-600 rounded-lg font-semibold text-[11px] hover:bg-opacity-90 active:scale-95 transition shadow-sm"
                                            >
                                                Override Lockout
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}